/**
 * CreateBuildStep — the rival-first "Build your challenge" screen
 * (specs/challenge-flow-redesign — Stories 2 & 3). Hosts the VS header, the
 * "One at a time" (deck) / "All games" (list) toggle, and the bottom Review bar.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text } from 'react-native';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { MIN_PICKS_TO_REVIEW } from '../../../src/create/hooks/useCreateFlowStep';
import { CREATE_FLOW_TEST_IDS } from '../../../src/create/constants/create.constants';
import { useFirstChallengeTarget } from '../../../src/onboarding/hooks/useFirstChallengeTarget';
import { FC_TARGET_KEYS } from '../../../src/onboarding/constants/firstChallengeTour.constants';
import { VsHeader } from './VsHeader';
import { CreateBuildDeck } from '../CreateBuildDeck/CreateBuildDeck';
import { CreateBuildList } from '../CreateBuildList/CreateBuildList';
import { buildStepStyles as styles } from './CreateBuildStep.styles';

export const CreateBuildStep: React.FC = () => {
  const {
    buildView,
    setBuildView,
    pickSlips,
    canReview,
    toReview,
  } = useCreatePage();

  const toDeck = useCallback(() => setBuildView('deck'), [setBuildView]);
  const toList = useCallback(() => setBuildView('list'), [setBuildView]);

  // First-challenge tour: spotlight the Review button (inert unless the tour is on that step).
  const { viewRef: reviewRef } = useFirstChallengeTarget(FC_TARGET_KEYS.review);

  const pickCount = pickSlips.length;
  const reviewLabel = useMemo(() => {
    if (canReview) return `Review ${pickCount} Pick${pickCount === 1 ? '' : 's'} ›`;
    return `Pick at least ${MIN_PICKS_TO_REVIEW} game${MIN_PICKS_TO_REVIEW === 1 ? '' : 's'} to continue`;
  }, [canReview, pickCount]);

  return (
    <View style={styles.container} testID={CREATE_FLOW_TEST_IDS.buildStep}>
      <VsHeader />

      <View style={styles.toggleRow}>
        <SoundPressable
          tapSoundId={SOUND_IDS.UI_TAP_NAV}
          onPress={toDeck}
          style={[styles.toggleBtn, buildView === 'deck' && styles.toggleBtnActive]}
          testID={CREATE_FLOW_TEST_IDS.buildToggleDeck}
        >
          <Text style={[styles.toggleText, buildView === 'deck' && styles.toggleTextActive]}>
            One at a time
          </Text>
        </SoundPressable>
        <SoundPressable
          tapSoundId={SOUND_IDS.UI_TAP_NAV}
          onPress={toList}
          style={[styles.toggleBtn, buildView === 'list' && styles.toggleBtnActive]}
          testID={CREATE_FLOW_TEST_IDS.buildToggleList}
        >
          <Text style={[styles.toggleText, buildView === 'list' && styles.toggleTextActive]}>
            All games
          </Text>
        </SoundPressable>
      </View>

      <View style={styles.body}>
        {buildView === 'deck' ? <CreateBuildDeck /> : <CreateBuildList />}
      </View>

      <View style={styles.footer}>
        <SoundPressable
          ref={reviewRef}
          tapSoundId={SOUND_IDS.UI_TAP_NAV}
          onPress={toReview}
          disabled={!canReview}
          style={[styles.reviewBtn, !canReview && styles.reviewBtnDisabled]}
          testID={CREATE_FLOW_TEST_IDS.buildReviewButton}
        >
          <Text style={[styles.reviewBtnText, !canReview && styles.reviewBtnTextDisabled]}>
            {reviewLabel}
          </Text>
        </SoundPressable>
      </View>
    </View>
  );
};
